import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Calendar, Clock, Send } from 'lucide-react';

interface BookDemoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BookDemoModal({ isOpen, onClose }: BookDemoModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    date: '',
    time: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // In a real implementation, this would be an API call
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      // Send email using your preferred email service
      const emailContent = `
        New Demo Request:
        Name: ${formData.name}
        Email: ${formData.email}
        Company: ${formData.company}
        Preferred Date: ${formData.date}
        Preferred Time: ${formData.time}
        Timezone: ${formData.timezone}
      `;

      // For now, we'll just log it
      console.log('Sending email to demo@agosidentity.com:', emailContent);
      
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setFormData({
          name: '',
          email: '',
          company: '',
          date: '',
          time: '',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
      }, 2000);
    } catch (err) {
      setError('Failed to schedule demo. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
      />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative z-10 w-full max-w-md p-6 bg-gray-800 rounded-lg shadow-xl"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-300"
        >
          <X className="h-6 w-6" />
        </button>

        <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
          <Calendar className="h-6 w-6 mr-2" />
          Schedule a Demo
        </h2>

        {success ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-8"
          >
            <div className="text-green-400 mb-4">
              <Send className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">Demo Scheduled!</h3>
            <p className="text-gray-300">
              We'll send you a confirmation email shortly.
            </p>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                Name
              </label>
              <input
                type="text"
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-1">
                Company
              </label>
              <input
                type="text"
                id="company"
                required
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-300 mb-1">
                Preferred Date
              </label>
              <input
                type="date"
                id="date"
                required
                min={new Date().toISOString().split('T')[0]}
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="time" className="block text-sm font-medium text-gray-300 mb-1">
                Preferred Time
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  id="time"
                  required
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Clock className="h-5 w-5 text-gray-400" />
              </div>
              <p className="mt-1 text-sm text-gray-400">
                Timezone: {formData.timezone}
              </p>
            </div>

            {/* Compliance Section */}
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 my-4">
              <div className="flex items-start gap-2 mb-3">
                <div className="text-yellow-500 mt-0.5">⚠️</div>
                <div className="text-xs text-yellow-200/80">
                  <strong className="text-yellow-500 block mb-1">DEMO ENVIRONMENT NOTICE</strong>
                  This is a public demo environment. Do not enter real personal data or sensitive information. Data sent here is for logic validation only.
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-start gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    required
                    className="mt-1 w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-800"
                  />
                  <span className="text-xs text-gray-400 group-hover:text-gray-300 transition-colors">
                    I understand this is a demo environment and agree not to submit sensitive personal information.
                  </span>
                </label>

                <label className="flex items-start gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    required
                    className="mt-1 w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-800"
                  />
                  <span className="text-xs text-gray-400 group-hover:text-gray-300 transition-colors">
                    I consent to the collection and processing of the data provided above for the purpose of scheduling a product demonstration, in accordance with the Data Privacy Act of 2012.
                  </span>
                </label>
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-900/50 border border-red-500/50 text-red-400 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Scheduling...' : 'Schedule Demo'}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}